import React, {FC, useState} from "react"
import qs from "qs"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {MagicLogin, MagicLoginStatus} from "@irati/types"
import tracking, {LoginType} from "@irati-tracking/platform/platform"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Flex from "@irati/ui/src/Flex"
import {Body, H3} from "@irati/ui/src/Typography"
import {RouterLink} from "@irati/ui/src/Link"
import theme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import ssoErrorHandler from "@irati/sso/services/errors/ssoErrorHandler"
import {initiateSSOLogin} from "@irati/sso/helpers/handleSSOFlow"
import {getAuthURL} from "@irati/sso/services/sso"
import session from "@irati/auth/services/session"
import tokens from "@irati/auth/services/tokens"

import {StyledImage} from "../Login/AccountLogin/styled"
import LoginBlock from "../../components/LoginBlock"

import {BusinessLogo, StyledCard} from "./styled"
import {ScreenTabs} from "."

interface Props extends InjectIntl {
  logins: MagicLogin[]
  ssoTenant?: MagicLogin
  loginWithPhone: boolean
  onTabChange: (screen: string) => void
}
interface QueryParams {
  redirectTo?: string
}
const DomainSelector: FC<Props> = ({logins, loginWithPhone, ssoTenant, formatMessage, onTabChange}) => {
  const [showAlert, setShowAlert] = useState(false)
  const [showSso, setShowSSO] = useState(!!ssoTenant)
  const [selectedDomain, setSelectedDomain] = useState<MagicLogin | undefined>(ssoTenant)
  const [errorMsg, setErrorMsg] = useState("")
  const [submit, setSubmit] = useState<Promise<any> | null>(null)
  const [ssoURL, setSsoURL] = useState<string | null>(null)
  const [adminLoginAlert, setAdminLoginAlert] = useState(false)

  const handleDomainSelector = (login: MagicLogin) => {
    setShowAlert(false)
    setAdminLoginAlert(false)
    setErrorMsg("")
    handleLoginStatus(login)
  }

  const handleLoginStatus = (login: MagicLogin) => {
    switch (login.status) {
      case MagicLoginStatus.MagicLoginStatusSuccess:
        return loginOTPCode(login)
      case MagicLoginStatus.MagicLoginStatusPasswordLoginDisabled:
        return handleLoginSSO(login)
      case MagicLoginStatus.MagicLoginStatusUserDisabled:
        return handleErrorMsg(formatMessage({id: "platform.oneTimePassword.error.userDeactivated"}))
      case MagicLoginStatus.MagicLoginStatusBusinessDisabled:
        return handleErrorMsg(formatMessage({id: "platform.oneTimePassword.error.businessDeactivated"}))
      case MagicLoginStatus.MagicLoginStatusAdminPhoneLoginDisabled:
        return handleAdminAlert()
      default:
        return handleErrorMsg(formatMessage({id: "platform.oneTimePassword.error.unknown"}))
    }
  }

  const handleAdminAlert = () => {
    setAdminLoginAlert(true)
    tracking.loginOTPError("Admin cannnot login")
  }

  const handleLoginSSO = (login: MagicLogin) => {
    tracking.loginOTPSubmit("sso", LoginType.ssoLogin)
    localStorage.setItem("login_detail", "sso sso_login")
    localStorage.setItem("login_type", LoginType.ssoLogin)
    getAuthURL(login.business?.uuid!, undefined, {})
      .then(({url}) => {
        setShowSSO(true)
        setSelectedDomain(login)
        setSsoURL(url!)
      })
      .catch((err) => {
        const message = ssoErrorHandler(err)
        setShowAlert(true)
        setErrorMsg(message)
        tracking.loginOTPError("sso", LoginType.ssoLogin)
        tracking.loginErrorDetails(message, LoginType.ssoLogin)
      })
  }

  const handleErrorMsg = (error: string) => {
    tracking.loginOTPError(loginWithPhone ? "otp_phone" : "otp")
    tracking.loginErrorDetails(error)
    setShowAlert(true)
    setErrorMsg(error)
  }

  const redirectToSubdomain = (accessToken: string, subdomain: string) => {
    tokens.set({accessToken})
    const query = qs.parse(window.location.search.replace("?", "")) as QueryParams
    const pathname = query.redirectTo ? `/welcome/login/code?redirectTo=${encodeURIComponent(query.redirectTo)}` : "/welcome/login/code"
    return session.postCrossDomain(subdomain, {accessToken}, {pathname})
  }

  const loginOTPCode = (login: MagicLogin) => {
    const accessToken = login?.auth?.legacyToken
    const subdomain = login?.business?.subdomain
    if (accessToken && subdomain) {
      localStorage.setItem("login_detail", loginWithPhone ? "otp_phone phone_number_otp_login" : "otp email_otp_login")
      return redirectToSubdomain(accessToken, subdomain)
    } else {
      const unknownError = formatMessage({id: "platform.oneTimePassword.error.unknown"})
      handleErrorMsg(unknownError)
    }
  }

  const singleSignOn = () => {
    setShowAlert(false)
    setAdminLoginAlert(false)
    setErrorMsg("")

    const ssoSubmit: Promise<any> = initiateSSOLogin(ssoURL!, {useRedirect: false})
      .then((response) => {
        const {accessToken} = response
        return redirectToSubdomain(accessToken, selectedDomain.business.subdomain)
      })
      .catch((err) => {
        handleErrorMsg(ssoErrorHandler(err))
      })
    setSubmit(ssoSubmit)
  }

  const helpLinks = [
    <FormattedMessage
      id="platform.defaultLogin.multipleTenants.help.otherDomain"
      values={{
        link: (
          <RouterLink to="/welcome/login/domain">
            <FormattedMessage id="platform.defaultLogin.multipleTenants.help.other.text" />
          </RouterLink>
        )
      }}
    />,
    <RouterLink to="/welcome/accounts">
      <FormattedMessage id="platform.defaultLogin.multipleTenants.help.accounts" />
    </RouterLink>
  ]

  return (
    <>
      {!showSso && (
        <>
          {showAlert && (
            <Alert
              type={AlertTypes.error}
              title="platform.oneTimePassword.selectDomain.errorLogin"
              subTitle={errorMsg}
              onHide={() => setShowAlert(false)}
            />
          )}
          {adminLoginAlert && (
            <Alert
              type={AlertTypes.error}
              title="platform.oneTimePassword.error.adminLoginPhoneDisabled"
              link="platform.oneTimePassword.error.adminLoginPhoneDisabledLink"
              onClickLink={() => onTabChange(ScreenTabs.email)}
              onHide={() => setAdminLoginAlert(false)}
            />
          )}
          <H3 marginBottom={2} textAlign="center">
            <FormattedMessage id="platform.defaultLogin.multipleTenants.title" />
          </H3>
          <LoginBlock helpLinks={helpLinks}>
            {logins &&
              logins.map((login, i) => {
                return (
                  <StyledCard
                    key={i}
                    bgColor={theme.colors.greys.lightest}
                    padding={2}
                    marginBottom={3}
                    onClick={() => handleDomainSelector(login)}>
                    <Flex alignItems="center" justifyContent="center">
                      <StyledImage src={formatMessage({id: login.branding?.businessLogoUrl})} />
                      <Body small>{login.business?.businessName}</Body>
                    </Flex>
                  </StyledCard>
                )
              })}
          </LoginBlock>
        </>
      )}
      {showSso && selectedDomain && (
        <>
          {showAlert && (
            <Alert
              type={AlertTypes.error}
              title="platform.oneTimePassword.selectDomain.error.login"
              subTitle={errorMsg || "platform.oneTimePassword.selectDomain.error.login2"}
              onHide={() => setShowAlert(false)}
            />
          )}
          <Flex justifyContent="center" alignContent="center">
            <BusinessLogo src={formatMessage({id: selectedDomain.branding?.businessLogoUrl})} />
          </Flex>
          <H3 marginBottom textAlign="center">
            <FormattedMessage id="platform.globalLogin.fields.welcomeTo.header" />
          </H3>
          <Button marginTop={2} primary mobileFull full load={submit} onClick={singleSignOn}>
            <FormattedMessage id="platform.oneTimePassword.selectDomain.sso.btn" />
          </Button>
        </>
      )}
    </>
  )
}

export default injectIntl(DomainSelector)
